import React from "react";
import type * as io from "io-ts";
import { SHAREABLE_PREFIX } from "./urlTools";
import type { StorageType } from "./storages";
import { STORAGES, safeRead, safeKey, safeWrite } from "./storages";
import useShareable from "./hooks/useShareable";
import { isFuncState } from "./isFuncState";

const UNINITIALIZED = Symbol("UNINITIALIZED");

// https://github.com/facebook/react/issues/14490#issuecomment-451924162
function useRefWithInitialFunction<T>(init: () => T) {
  const ref = React.useRef<T | typeof UNINITIALIZED>(UNINITIALIZED);
  if (ref.current === UNINITIALIZED) {
    ref.current = init();
  }
  return ref as React.MutableRefObject<T>;
}

function computeKey(scope: string | number | undefined, key: string, shared: boolean) {
  return [shared ? SHAREABLE_PREFIX : false, key, scope === undefined ? false : String(scope)]
    .filter(Boolean)
    .join("~");
}

type DefaultSetter<T> = T | (() => T);

interface Options<T, S, O = T, I = unknown> {
  key: string;
  storage: StorageType;
  type: io.Type<T, O, I>;
  defaultValue?: DefaultSetter<T>;
  scoped: S;
}
type StrictOptions<T, S> = Options<T, S> & { defaultValue: DefaultSetter<T> };

type Hook<T> = readonly [T | undefined, (v: React.SetStateAction<T | undefined>) => void];
type StrictHook<T> = readonly [T, (v: React.SetStateAction<T>) => void];

type HookOptions<S> = S extends true
  ? { scope: string | number; defaultValue?: undefined }
  : { defaultValue?: undefined };
type StrictHookOptions<T, S> = S extends true
  ? { scope: string | number; defaultValue: DefaultSetter<T> }
  : { defaultValue: DefaultSetter<T> };

export default function createGlobalState<T, S extends boolean>(
  o: StrictOptions<T, S>,
): {
  (o: HookOptions<S>): StrictHook<T>;
};

export default function createGlobalState<T, S>(
  o: Options<T, S>,
): {
  (o: HookOptions<S>): Hook<T>;
  (o: StrictHookOptions<T, S>): StrictHook<T>;
};

export default function createGlobalState<T, S>({
  key: keyPrefix,
  type,
  storage: storageType,
  defaultValue,
}: Options<T, S>) {
  const shared = storageType === "session";
  const storage = STORAGES[storageType];
  safeKey(keyPrefix);
  const valueSetters: { [key: string]: React.Dispatch<React.SetStateAction<T | undefined>>[] } = {};

  return (options: HookOptions<S>) => {
    const key = computeKey(
      options && "scope" in options ? options.scope : undefined,
      keyPrefix,
      shared,
    );
    const valueRef = React.useRef<T | undefined>();
    const defaultValueRef = useRefWithInitialFunction(() => {
      const _default = defaultValue === undefined ? options.defaultValue : defaultValue;
      return isFuncState(_default) ? _default() : _default;
    });

    useShareable(key, shared);

    const [, rerender] = React.useState({});
    const setStateValue = React.useCallback(
      (newValue: React.SetStateAction<T | undefined>) => {
        valueRef.current = isFuncState(newValue)
          ? newValue(valueRef.current === undefined ? defaultValueRef.current : valueRef.current)
          : newValue;
        rerender({});
      },
      [defaultValueRef, valueRef],
    );

    const keyRef = React.useRef<string>();
    if (key !== keyRef.current) {
      valueRef.current = safeRead({ storage, type, key });
      keyRef.current = key;
    }

    React.useLayoutEffect(() => {
      valueSetters[key] = [...(valueSetters[key] ?? []), setStateValue];
      return () => {
        valueSetters[key] = (valueSetters[key] ?? []).filter(
          valueSetter => valueSetter !== setStateValue,
        );
      };
    }, [key, setStateValue]);

    React.useLayoutEffect(() => {
      if (storageType !== "local") return;
      const handleStorage = (e: StorageEvent) => {
        if (e.key === key) setStateValue(safeRead({ storage, type, key }));
      };
      window.addEventListener("storage", handleStorage);
      return () => window.removeEventListener("storage", handleStorage);
    }, [key, setStateValue]);

    const value = valueRef.current === undefined ? defaultValueRef.current : valueRef.current;

    const setValue = React.useCallback(
      (newValue: React.SetStateAction<T | undefined>) => {
        const key = keyRef.current;
        if (!key) throw new Error("Missing key");
        setStateValue(oldValue => {
          const value = isFuncState(newValue) ? newValue(oldValue) : newValue;
          safeWrite({ storage, value, key, type });
          return value;
        });
        for (const valueSetter of valueSetters[key] ?? []) {
          if (valueSetter !== setStateValue) valueSetter(newValue);
        }
      },
      [setStateValue],
    );

    return React.useMemo(() => [value, setValue] as const, [value, setValue]);
  };
}
